import i18n, { InitOptions, Module, Newable } from 'i18next';
import HttpApi from 'i18next-http-backend';
import { initReactI18next, Trans, useTranslation, UseTranslationResponse, withTranslation, WithTranslation } from 'react-i18next';
import en from './localization.json';
import { getLanguage, loadLocalization } from './utils';

export const DefaultLanguage = 'en';

export const resources = {
	[DefaultLanguage]: {
		translation: en
	}
} as const;

export interface I18NInitOptions extends InitOptions {
	modules?: Array<Module | Newable<Module> | Newable<Module>>;
}

export function initI18n(initOptions?: I18NInitOptions): Promise<typeof i18n> {
	const _i18n = i18n.createInstance();

	const _initOptions: I18NInitOptions = {
		partialBundledLanguages: true,
		fallbackLng: DefaultLanguage,
		lng: getLanguage(),
		resources,
		...initOptions
	};

	if (Array.isArray(initOptions?.modules)) {
		_initOptions.modules?.forEach((module) => _i18n.use(module));
	}

	_i18n.use(initReactI18next);

	return _i18n.init(_initOptions).then(() => _i18n);
}

export function initI18nLocalization(initOptions?: I18NInitOptions): Promise<typeof i18n> {
	return initI18n({
		modules: [HttpApi],
		backend: {
			loadPath: '{{lng}}',
			request: async (options, lng, payload, callback) => {
				try {
					loadLocalization(lng)
						.then((localization) => {
							if (!localization) {
								//unexpected
								callback({ message: 'Unexpected error.' }, null);
							}

							callback(null, {
								status: 200,
								data: JSON.stringify(localization)
							});
						})
						.catch((e) => {
							callback(e, null);
						});
				} catch (err) {
					callback(err, null);
				}
			}
		},
		interpolation: {
			format: (value, format) => {
				if (format === 'lowercase') {
					return value?.toLowerCase();
				}
				return value;
			}
		},
		...initOptions
	});
}

// react-i18next versions higher than 11.11.0
declare module 'react-i18next' {
	// and extend them!
	interface CustomTypeOptions {
		// custom resources type
		resources: (typeof resources)[typeof DefaultLanguage];
	}
}

export type { WithTranslation, UseTranslationResponse };
export { useTranslation, withTranslation, Trans };
